import classNames from 'classnames';
import { Avatar, Button, Card, message } from 'antd';
import { EditFilled, ShopOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { useCallback } from 'react';
import { KeyValue } from 'components/common/KeyValue';
import { Profit } from 'components/common/Profit';
import { useOrderActions } from 'hooks/api';
import APP_ROUTES from 'config/routes';
import { STORE_URL } from 'config/constants';

import styles from './index.module.scss';

export interface OrderInfoProps {
  order: Order.Item
  className: string;
}

export const OrderInfo = ({ order, className }: OrderInfoProps) => {
  const { process } = useOrderActions();
  const handleProcess = useCallback(() => {
    process({ orderId: order._id }, {
      onSuccess: () => message.success('Order successfully processed!'),
      onError: (e) => message.error(e.message || 'Order processing error'),
    });
  }, []);
  const renderItem = useCallback((item: Order.OrderItem, idx) => {
    const value = item.product.values?.find((v) => v.originPrice === item.originPrice)
      || item.product.values?.[0];
    return (
      <div key={idx.toString()} className={styles.item}>
        <div className={styles.avatar}>
          <Avatar src={item.product.image} />
          <div className={styles.avatarTitle}>
            {item.product.name}
            &nbsp;
            $
            {item.displayName || item.originPrice / 100}
          </div>
          <Profit value={item.profit / 100} />
        </div>
        <KeyValue title='Amount' value={`x${item.amount}`} />
        <KeyValue title='Price' value={`$${item.price / 100}`} />
        {item.delivered ? '(DELIVERED)' : null}
        <div className={styles.productLinks}>
          <Link
            aria-label='open in admin'
            to={APP_ROUTES.PRODUCT_EDIT.replace(':id', item.product._id)}
            target='_blank'
            rel='noopener noreferrer'
          >
            <EditFilled />
            {' '}
            Admin
          </Link>
          {value?.link && (
            <a
              aria-label='open in store'
              href={`${STORE_URL}/product/${value.link}`}
              target='_blank'
              rel='noopener noreferrer'
            >
              <ShopOutlined />
              {' '}
              Store
            </a>
          )}
        </div>
      </div>
    );
  }, []);
  return (
    <Card
      title='Order info'
      className={classNames(styles.card, className)}
      actions={[
        <Button type='primary' onClick={handleProcess}>Process order</Button>,
        <Button disabled type='primary'>Cancel order</Button>,
      ]}
    >
      <KeyValue title='Id' value={order._id} />
      <KeyValue title='Status' value={order.status} />
      <KeyValue title='Ip' value={order.ip} />
      <KeyValue title='Created date' value={dayjs(order.createdAt).format('MMMM D, YYYY h:mm A')} />
      <KeyValue title='Profit' className={styles.profit} value={`$${(order.profit / 100).toFixed(2).toString()}`} />
      <Card title='Items info'>
        <div className={styles.items}>
          {order.items.map(renderItem)}
        </div>
      </Card>
    </Card>
  );
};
